import React, { useState } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import BaseModal from 'components/UI/Modal/BaseModal';
import Button from 'components/UI/V3/Button/Button';
import { ConfirmDialog } from 'components/UI/ConfirmDialog/ConfirmDialog';
import { Logo } from 'components/UI/Layout/Logo';
import { useEmailMarketing } from '../EmailMarketingProvider';
import { CampaignDraft } from '../types';
import { Stepper } from '../UI/Stepper/Stepper';
import { EmailPreview } from '../UI/EmailPreview/EmailPreview';
import LocationsStep from './steps/LocationsStep';
import AudienceStep from './steps/AudienceStep';
import ContentStep from './steps/ContentStep';
import ReviewStep from './steps/ReviewStep';
import SummaryPanel from './SummaryPanel';
import SendConfirmModal from './SendConfirmModal';
import { useAudienceData } from './useAudienceData';
import SentScreen from './SentScreen';
import styles from './CampaignWizard.module.scss';

const STEPS = [
    { id: 'locations', label: 'Locations' },
    { id: 'audience', label: 'Audience' },
    { id: 'content', label: 'Content' },
    { id: 'review', label: 'Review & send' }
];

interface CampaignWizardProps {
    initialDraft: CampaignDraft;
}

export const CampaignWizard: React.FC<CampaignWizardProps> = ({ initialDraft }) => {
    const { templates, locationName, closeWizard, saveDraft, sendCampaign, saving } = useEmailMarketing();

    const [draft, setDraft] = useState<CampaignDraft>(initialDraft);
    const [step, setStep] = useState(0);
    const [maxReached, setMaxReached] = useState(initialDraft.id ? STEPS.length - 1 : 0);
    const [confirmingExit, setConfirmingExit] = useState(false);
    const [confirmingSend, setConfirmingSend] = useState(false);
    const [sent, setSent] = useState(false);
    const [sentRecipients, setSentRecipients] = useState(0);

    // Recipient counts from the audience endpoints.
    const audience = useAudienceData(draft);
    const { estimate } = audience;
    const template = templates.find(item => item.id === draft.templateId) ?? null;

    // Changing locations changes the recipient pool, so manual picks no longer apply.
    const update = (patch: Partial<CampaignDraft>) =>
        setDraft(prev => ({
            ...prev,
            ...(patch.locationIds ? { selectedCustomerIds: 'all' as const, excludedCustomerIds: [] } : {}),
            ...patch
        }));

    const canContinue = [
        draft.name.trim().length > 0 && draft.locationIds.length > 0,
        (!estimate.known || estimate.total > 0) && (draft.recipientType !== 'client_groups' || draft.tags.length > 0),
        draft.templateId !== null && draft.subject.trim().length > 0,
        true
    ][step];

    const previewFrom =
        draft.locationIds.length > 1
            ? `${locationName(draft.locationIds[0])} +${draft.locationIds.length - 1} more`
            : locationName(draft.locationIds[0] ?? null);

    const goTo = (index: number) => {
        setStep(index);
        setMaxReached(prev => Math.max(prev, index));
    };

    const handleContinue = () => {
        if (step === STEPS.length - 1) {
            setConfirmingSend(true);
            return;
        }
        goTo(step + 1);
    };

    const requestClose = () => {
        if (sent || (!draft.name && step === 0)) {
            closeWizard();
            return;
        }
        setConfirmingExit(true);
    };

    const isLastStep = step === STEPS.length - 1;
    const showPreviewAside = step >= 2;

    return (
        <BaseModal
            isOpen
            onClose={requestClose}
        >
            <BaseModal.Header showDefaultLayout={false}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <Logo className={styles.logo} />
                        <span className={styles.draftName}>{draft.name || 'New campaign'}</span>
                    </div>

                    <Stepper
                        steps={STEPS}
                        currentIndex={sent ? STEPS.length : step}
                        maxReachedIndex={sent ? STEPS.length : maxReached}
                        onStepClick={sent ? undefined : goTo}
                        className={styles.stepper}
                    />

                    <div className={styles.headerRight}>
                        <button
                            type="button"
                            aria-label="Close"
                            className={styles.close}
                            onClick={requestClose}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>
            </BaseModal.Header>

            {sent ? (
                <SentScreen
                    name={draft.name}
                    recipients={sentRecipients}
                    locations={draft.locationIds.length}
                    onDone={closeWizard}
                />
            ) : (
                <>
                    <div className={styles.body}>
                        <div className={clsx(styles.layout, { [styles.layoutWide]: showPreviewAside })}>
                            <main className={styles.main}>
                                {step === 0 && (
                                    <LocationsStep
                                        draft={draft}
                                        onChange={update}
                                    />
                                )}
                                {step === 1 && (
                                    <AudienceStep
                                        draft={draft}
                                        audience={audience}
                                        onChange={update}
                                    />
                                )}
                                {step === 2 && (
                                    <ContentStep
                                        draft={draft}
                                        onChange={update}
                                    />
                                )}
                                {step === 3 && (
                                    <ReviewStep
                                        draft={draft}
                                        estimate={estimate}
                                        template={template}
                                        onEditStep={goTo}
                                    />
                                )}
                            </main>

                            <aside className={styles.aside}>
                                {showPreviewAside ? (
                                    template ? (
                                        <EmailPreview
                                            className={styles.preview}
                                            html={template.html}
                                            fromName={previewFrom}
                                            subject={draft.subject}
                                            previewText={draft.previewText}
                                        />
                                    ) : (
                                        <div className={styles.previewEmpty}>
                                            <strong>No template selected</strong>
                                            <span>Pick a template to see a live preview.</span>
                                        </div>
                                    )
                                ) : (
                                    <SummaryPanel
                                        draft={draft}
                                        estimate={estimate}
                                        template={template}
                                    />
                                )}
                            </aside>
                        </div>
                    </div>

                    <footer className={styles.footer}>
                        <button
                            type="button"
                            className={styles.saveDraft}
                            disabled={saving}
                            onClick={() => saveDraft(draft)}
                        >
                            Save as draft
                        </button>
                        <div className={styles.footerActions}>
                            {step > 0 && (
                                <Button
                                    variant="outlined"
                                    label="Back"
                                    onClick={() => setStep(step - 1)}
                                />
                            )}
                            <Button
                                variant="black"
                                label={isLastStep ? 'Send campaign' : 'Continue'}
                                disabled={!canContinue}
                                onClick={handleContinue}
                            />
                        </div>
                    </footer>
                </>
            )}

            {confirmingSend && (
                <SendConfirmModal
                    name={draft.name}
                    recipients={estimate.known ? estimate.total : null}
                    locations={draft.locationIds.length}
                    duplicates={estimate.duplicates}
                    sending={saving}
                    onCancel={() => setConfirmingSend(false)}
                    onConfirm={async () => {
                        const result = await sendCampaign(draft);
                        // Keep the saved id so a retry after an error updates the same draft.
                        if (result.campaignId !== draft.id) {
                            setDraft(prev => ({ ...prev, id: result.campaignId }));
                        }
                        setConfirmingSend(false);
                        if (result.sent) {
                            setSentRecipients(result.recipients);
                            setSent(true);
                        }
                    }}
                />
            )}

            <ConfirmDialog
                open={confirmingExit}
                className={styles.exitDialog}
                title="Leave campaign?"
                question="Save it as a draft to finish later, or discard your changes."
                onCancel={() => setConfirmingExit(false)}
                actions={
                    <div className={styles.exitActions}>
                        <Button
                            variant="outlined"
                            label="Discard"
                            onClick={closeWizard}
                        />
                        <Button
                            variant="black"
                            label="Save draft"
                            disabled={saving}
                            onClick={async () => {
                                // On an API error the dialog closes and the wizard stays open with the toast.
                                if (!(await saveDraft(draft))) setConfirmingExit(false);
                            }}
                        />
                    </div>
                }
            />
        </BaseModal>
    );
};

export default CampaignWizard;
